import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useModulos } from '@/hooks/useModulos';
import { useCatalogoSuscripcion } from '@/queries/subscription.queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCOP } from '@/lib/utils';

/**
 * Una pantalla de un módulo que la empresa no contrató.
 *
 * El menú ya escondía el módulo, pero escribir la dirección abría la pantalla
 * igual, y desde el 14/10/2026 la base la deja vacía (`tiene_modulo` en las
 * políticas): se veía un hotel sin habitaciones y nadie sabía por qué. Aquí se
 * dice qué falta, cuánto cuesta y dónde se agrega.
 *
 * Si la consulta de módulos falla, se deja pasar: un fallo de red no es «no
 * lo tienes», y la base decide de todos modos.
 */
export function RequiereModulo({ modulo, children }: { modulo: string | string[]; children: ReactNode }) {
  const { tiene, cargando } = useModulos();
  const catalogo = useCatalogoSuscripcion();

  if (cargando) return <Skeleton className="h-64 w-full" />;
  if (tiene(modulo)) return <>{children}</>;

  const primero = Array.isArray(modulo) ? modulo[0] : modulo;
  const info = catalogo.data?.modulos.find((m) => m.code === primero);
  return (
    <Card className="mx-auto mt-8 max-w-lg">
      <CardContent className="space-y-4 p-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
          <Lock className="h-6 w-6" />
        </div>
        <div>
          <h2 className="font-display text-xl font-bold">
            Tu plan no incluye {info?.name ?? 'este módulo'}
          </h2>
          {info?.description && <p className="mt-1 text-sm text-muted-foreground">{info.description}</p>}
          {info && info.priceCents > 0 && (
            <p className="mt-2 text-sm">
              Puedes agregarlo por <strong>{formatCOP(info.priceCents)}</strong> al mes.
            </p>
          )}
        </div>
        <Button asChild>
          <Link to="/subscription">Ver cómo agregarlo</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
